// Abspiel-Berichte (Proof of Play): Typen und Abfragen.
import { BASE, api } from './client';
import { useTenantQuery } from './queries';

export interface ReportFilter {
  from: string;
  to: string;
  locationId?: string;
  groupId?: string;
  deviceId?: string;
  playlistId?: string;
  mediaId?: string;
}

export interface ReportTotals {
  plays: number;
  durationMs: number;
  share: number;
  screens: number;
  failed: number;
  interrupted: number;
  displaced: number;
  screenTimeMs: number;
}

export interface ContentRow { type: 'media' | 'layout' | 'unknown'; id: string | null; name: string; kind: string | null; deleted: boolean; plays: number; zonePlays: number; durationMs: number; share: number }
export interface ScreenRow { deviceId: string; name: string; locationName: string | null; plays: number; durationMs: number; share: number }
export interface TableRow { date: string; deviceId: string; deviceName: string; locationName: string | null; type: ContentRow['type']; contentId: string | null; contentName: string; playlistName: string | null; plays: number; durationMs: number }

export interface Report {
  from: string;
  to: string;
  totals: ReportTotals;
  byDay: { date: string; plays: number; durationMs: number }[];
  byHour: { hour: number; plays: number; durationMs: number }[];
  topContent: ContentRow[];
  screens: ScreenRow[];
  table: TableRow[];
  tableTruncated: boolean;
  rawSince: string | null;
}

export type EndReason = 'completed' | 'failed' | 'interrupted' | 'emergency' | 'power_off' | 'stopped';
export interface PlayRow {
  id: string; startedAt: string; timezone: string; durationMs: number; plays: number; compacted: 'hour' | 'day' | null; endReason: EndReason;
  deviceId: string; deviceName: string; locationName: string | null; playlistName: string | null; layoutName: string | null; zone: string | null;
}
export interface MediaPlays { media: { id: string; name: string; kind: string; deleted: boolean }; items: PlayRow[]; nextCursor: string | null }

export interface ReportOptions {
  locations: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  devices: { id: string; name: string; locationId: string | null }[];
  playlists: { id: string; name: string }[];
  media: { id: string; name: string; kind: string; deleted: boolean }[];
  timezone: string;
}

export interface ReportSettings {
  rawRetentionMonths: number;
  aggregateRetentionMonths: number;
  monthlyEnabled: boolean;
  monthlyRecipients: string[];
  monthlyLocale: 'de' | 'en';
  rowVersion: number;
  smtpConfigured: boolean;
  timezone: string;
}

export const rk = {
  options: ['reports', 'options'] as const,
  report: (q: string) => ['reports', 'playback', q] as const,
  settings: ['reports', 'settings'] as const,
};

/** Query-String aus Filtern (leere Werte weggelassen). */
export const filterQuery = (f: Partial<ReportFilter> & { lang?: string }) =>
  new URLSearchParams(Object.entries(f).filter((e): e is [string, string] => typeof e[1] === 'string' && !!e[1])).toString();

export const useReportOptions = () => useTenantQuery(rk.options, () => api.get<ReportOptions>('/reports/options'));
export const useReport = (f: ReportFilter, enabled = true) => {
  const q = filterQuery(f);
  return useTenantQuery(rk.report(q), () => api.get<Report>(`/reports/playback?${q}`), { enabled });
};
export const useReportSettings = () => useTenantQuery(rk.settings, () => api.get<ReportSettings>('/reports/settings'));
export const getMediaPlays = (mediaId: string, f: Omit<ReportFilter, 'mediaId'>, cursor?: string) =>
  api.get<MediaPlays>(`/reports/media/${mediaId}/plays?${filterQuery({ ...f, limit: '100', ...(cursor ? { cursor } : {}) } as Record<string, string>)}`);

/** Download-Adressen (direkter Link, Sitzungs-Cookie genügt für GET). */
export const exportUrl = (f: ReportFilter, format: 'csv' | 'pdf', lang: string) => `${BASE}/reports/playback/export.${format}?${filterQuery({ ...f, lang })}`;
export const playsExportUrl = (mediaId: string, f: Omit<ReportFilter, 'mediaId'>, format: 'csv' | 'pdf', lang: string) =>
  `${BASE}/reports/media/${mediaId}/plays/export.${format}?${filterQuery({ ...f, lang })}`;
